import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { superAdminApi } from '../../api/superAdmin.js';
import { MonthPager, currentMonthTaipei } from '../../components/SuperAdmin/MonthPager.jsx';
import { coverFromSeed, initialsOf } from '../../components/IvrAgents/coverArt.js';
import { getCached, setCached, invalidate } from './pageCache.js';
import { accountsApi } from '../../api/accounts.js';
import { overlayDismiss } from '../../lib/overlayDismiss';
import { Globe2, ChevronRight, Search, Download, Loader2, X, Pencil } from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';

/**
 * /admin/data（role=dealer 經銷商數據總覽）/ /admin/business（role=business 商用帳號總覽）
 * 卡片式（同超管中心視覺語言）：每張 tenant 卡＝封面漸層+縮寫+當月四格數據，點卡看通話記錄。
 * 商用版 section header 多帶：席次（AA/CS）與所屬經銷商。
 * 前端快取 per 月份 — 切頁回來不重抓，refresh / 換月才載入。
 */
// 「分鐘」一律用計費口徑（= 月帳單 billing/overview 同一套）：billableSec 已扣「轉真人 ring 等待」、
// 網頁語音 ×webMinuteFactor（0.5）、工作室模擬（ivr_test）與網頁文字客服（web_text）不計。
// 折算由後端 stats-overview 做好（WEB_MINUTE_FACTOR 是後端 plans.js 的單一來源）。
// 舊 payload / 前端快取殘留沒有 billableSec → 退回原始 durationSec，寧可偏高也不要顯示 0。
// 進位一律 Math.ceil —— 跟月帳單 saas-admin/billing/overview 的 minutesUsed 同一套。
// （曾經三邊各行其是：帳單 ceil、客戶後台 floor、這頁 round → 同一個月會看到 469/470 兩種答案。）
const billMin = (x) => Math.ceil(((x?.billableSec ?? x?.durationSec ?? 0)) / 60);

/**
 * 帳號要顯示的「公司名稱」。
 *
 * 商用帳號自己的 displayName 幾乎都是空的（實查 46 個帳號只有 1 個有），真正認得出是哪一家的
 * 是**租戶**的 displayName（72 個租戶有 70 個有值：台北榮總 / 福邦 / 遠傳 / 東訊_品鑑部…）。
 * 所以一律以租戶名為主，帳號自己的 displayName 只是備援。
 *
 * 多租戶時取共同前綴（宏華 CTI01 + 宏華 CTI02 → 宏華），並去掉尾端空白與英數字尾碼避免過長。
 * 原本這段只寫在長條圖裡，卡片沒用到 → PM 0903 P7「補名稱」；抽出來兩邊共用，不會再分岔。
 */
function accountLabel(a) {
    const names = (a?.tenants || []).map((tn) => tn.displayName).filter(Boolean);
    if (!names.length) return a?.displayName || '';
    if (names.length === 1) return names[0];
    let prefix = names[0];
    for (const n of names.slice(1)) {
        let i = 0;
        while (i < prefix.length && i < n.length && prefix[i] === n[i]) i++;
        prefix = prefix.slice(0, i);
    }
    prefix = prefix.replace(/[\s\w-]+$/g, '').trim();
    return prefix || names[0];
}

export default function CustomerDataPage({ role = 'dealer' }) {
    const { t } = useTranslation('superAdmin');
    const k = role === 'business' ? 'biz' : 'data';   // i18n 標題組
    const [month, setMonth] = useState(currentMonthTaipei());
    const [data, setData] = useState(() => getCached(`sa:data:${currentMonthTaipei()}`));
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(!data);
    const [q, setQ] = useState('');   // 搜尋：帳號 email / 顯示名 / 租戶 hostId 或名稱（PM 0903 P6+P7）

    useEffect(() => {
        const hit = getCached(`sa:data:${month}`);
        if (hit) { setData(hit); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        superAdminApi.statsOverview(month)
            .then((d) => { if (!cancelled) { setData(setCached(`sa:data:${month}`, d)); setError(null); } })
            .catch((e) => { if (!cancelled) setError(e); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [month]);

    // 席次改完：席次會影響每個月份的額度，超管中心總覽也顯示席次 → 超管頁面的快取全部作廢，
    // 畫面上這個帳號先就地換成新值
    const onSeatsSaved = (uid, seats) => {
        invalidate('sa:');
        setData((d) => (d ? { ...d, accounts: (d.accounts || []).map((a) => (a.uid === uid ? { ...a, seats } : a)) } : d));
    };

    const ofRole = useMemo(
        () => (data?.accounts || []).filter((a) => (a.accountRole || 'dealer') === role),
        [data, role]
    );
    // 搜尋在前端做 —— accounts 本來就整包在記憶體裡，打字即篩不用重打 API。
    // 比對範圍刻意含租戶：超管常常只記得「vghtpe」或「台北榮總」，記不得是掛在哪個 email 底下。
    const accounts = useMemo(() => {
        const kw = q.trim().toLowerCase();
        if (!kw) return ofRole;
        return ofRole.filter((a) => [
            a.email, a.displayName, accountLabel(a),
            ...(a.tenants || []).flatMap((tn) => [tn.hostId, tn.displayName]),
        ].some((v) => v && String(v).toLowerCase().includes(kw)));
    }, [ofRole, q]);
    // 全帳號 email map（商用帳號的 createdBy → 經銷商 email）
    const emailByUid = useMemo(() => {
        const m = {};
        for (const a of data?.accounts || []) m[a.uid] = a.email;
        return m;
    }, [data]);
    // 本頁統計只算這個 role 的帳號
    // ⚠️ 用 ofRole 不是 accounts：上方那排是全域概況，搜尋時不該跟著跳（會讓人以為資料不見了）
    const grand = useMemo(() => {
        const g = { calls: 0, durationSec: 0, billableSec: 0, nonBillableSec: 0, inboundCalls: 0, outboundCalls: 0 };
        for (const a of ofRole) for (const key of Object.keys(g)) g[key] += a.totals?.[key] || 0;
        return g;
    }, [ofRole]);

    return (
        <div>
            <PageHeader
                eyebrow={`super admin · ${role === 'business' ? 'business accounts' : 'resellers'}`}
                title={t(`${k}.title`)}
                description={t(`${k}.description`)}
                actions={
                    <div className="flex items-center gap-2">
                        <SearchBox value={q} onChange={setQ} placeholder={t(`${k}.searchPh`)} />
                        <MonthPager month={month} onChange={setMonth} />
                    </div>
                }
            />
            {error && (
                <div className="mb-6 px-4 py-3 rounded-md border border-danger-500/30 bg-danger-100/40 text-sm text-danger-500">
                    {String(error.message || error)}
                </div>
            )}

            <div className="mb-8 flex items-stretch divide-x divide-cream-200 border-y border-cream-200">
                <Stat label={t(`${k}.stat.accounts`)} value={accounts.length} />
                <Stat label={t('data.stat.calls')} value={grand.calls ?? '—'} />
                <Stat label={t('data.stat.minutes')} value={grand.durationSec != null ? billMin(grand).toLocaleString() : '—'} />
                <Stat label={t('data.stat.inbound')} value={grand.inboundCalls ?? '—'} />
                <Stat label={t('data.stat.outbound')} value={grand.outboundCalls ?? '—'} />
            </div>
            {/* 口徑註記 — 這頁的「分鐘」跟月帳單同一套，跟客戶後台計費卡對得起來 */}
            <div className="-mt-6 mb-8 text-[11px] text-ink-400 leading-relaxed">
                {t('data.minutesNote', { factor: data?.webMinuteFactor ?? 0.5 })}
            </div>

            {/* 用量比較直條圖（分鐘；tooltip 帶通數/進線/外播）— 全零就不畫空圖 */}
            {accounts.some((a) => (a.totals?.calls || 0) > 0) && (
                <div className="mb-10">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 mb-3">{t(`${k}.chartTitle`)}</div>
                    <UsageChart accounts={accounts} t={t} />
                </div>
            )}

            {loading && !data ? (
                <div className="text-center py-20 text-ink-400 font-mono text-xs">Loading…</div>
            ) : accounts.length === 0 ? (
                <div className="py-16 text-center text-sm text-ink-400 italic">{t(`${k}.empty`)}</div>
            ) : (() => {
                // 數據頁主軸是「用量」不是 PBX 狀態：按當月通數排序（多→少），
                // 零用量的帳號沉底收成「本月無用量」一組（沒 PBX 但有 webcall 用量的照常排前面）
                const sorted = [...accounts].sort((a, b) => (b.totals?.calls || 0) - (a.totals?.calls || 0));
                const active = sorted.filter((a) => (a.totals?.calls || 0) > 0);
                const inactive = sorted.filter((a) => !(a.totals?.calls || 0));
                const Section = ({ acc }) => (
                    <section>
                        <header className="flex items-baseline gap-3 pb-2 border-b border-cream-200 flex-wrap">
                            <h2 className="heading-display text-lg font-semibold text-ink-900 tracking-tight">{acc.email}</h2>
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent-700">
                                {t(`role.${acc.accountRole}`, { defaultValue: acc.accountRole })}
                            </span>
                            {/* 商用帳號補：席次（AA/CS）＋所屬經銷商 */}
                            {role === 'business' && acc.seats && (
                                <span className="text-[11px] text-ink-500 tnum">AA {acc.seats.plan_01 ?? 0} · CS {acc.seats.plan_02 ?? 0}</span>
                            )}
                            {role === 'business' && acc.createdBy && emailByUid[acc.createdBy] && (
                                <span className="text-[11px] text-ink-400">{t('biz.ownedBy', { email: emailByUid[acc.createdBy] })}</span>
                            )}
                            <span className="ml-auto tnum text-xs text-ink-500">
                                {t('data.subtotal', { calls: acc.totals.calls, min: billMin(acc.totals) })}
                            </span>
                        </header>
                        <div className="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {acc.tenants.map((tn) => <UsageCard key={tn.hostId} tn={tn} kind={role === 'business' ? 'business' : 'full'} />)}
                        </div>
                    </section>
                );
                // 商用帳號總覽：以「所屬經銷商」分組（PM 0903 P8），組內每個帳號一張企業藍卡片。
                // 直客掛在 osense 自己的經銷帳號底下（qubby_ivr@osensetech.com 就是那個帳號，
                // 33 個商用帳號都掛它），所以不需要另開「未歸屬」群組 —— 除非 createdBy 指到
                // 已被刪掉的帳號，那種才落到 unknown 群。
                if (role === 'business') {
                    const groups = new Map();
                    for (const acc of sorted) {
                        const key = acc.createdBy && emailByUid[acc.createdBy] ? acc.createdBy : '_unknown';
                        if (!groups.has(key)) groups.set(key, []);
                        groups.get(key).push(acc);
                    }
                    // 組別排序：用量大的經銷商在前；未歸屬永遠沉底
                    const minutesOf = (list) => list.reduce((s2, a) => s2 + billMin(a.totals), 0);
                    const ordered = [...groups.entries()].sort(([ka, la], [kb, lb]) => (
                        (ka === '_unknown') - (kb === '_unknown') || minutesOf(lb) - minutesOf(la)
                    ));
                    return (
                        <div className="space-y-10">
                            {ordered.map(([dealerUid, list]) => (
                                <section key={dealerUid}>
                                    <header className="flex items-baseline gap-3 pb-2 border-b border-cream-200 flex-wrap">
                                        <h2 className="heading-display text-lg font-semibold text-ink-900 tracking-tight">
                                            {dealerUid === '_unknown' ? t('biz.noDealer') : emailByUid[dealerUid]}
                                        </h2>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent-700">
                                            {t('role.dealer')}
                                        </span>
                                        <span className="ml-auto tnum text-xs text-ink-500">
                                            {t('biz.groupSubtotal', { n: list.length, min: minutesOf(list).toLocaleString() })}
                                        </span>
                                    </header>
                                    <div className="mt-4 grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                                        {list.map((acc) => (
                                            <BusinessAccountCard key={acc.uid} acc={acc} dealerEmail={emailByUid[acc.createdBy]} month={month} t={t} onSeatsSaved={onSeatsSaved} />
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    );
                }
                return (
                    <div className="space-y-10">
                        {active.map((acc) => <Section key={acc.uid} acc={acc} />)}
                        {inactive.length > 0 && (
                            <>
                                <header className="flex items-baseline gap-3 pb-2 border-b border-cream-200">
                                    <h2 className="heading-display text-base font-semibold text-ink-500">{t('data.zeroGroup')}</h2>
                                    <span className="text-xs text-ink-400 tnum">{inactive.length}</span>
                                </header>
                                {/* 零用量：精簡列即可，不用整排空卡片 */}
                                <div className="rounded-lg border border-cream-200 bg-white/30 divide-y divide-cream-100">
                                    {inactive.map((acc) => (
                                        <div key={acc.uid} className="flex items-center gap-3 px-4 py-2.5 flex-wrap">
                                            <span className="text-sm text-ink-500">{acc.email}</span>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-400">
                                                {t(`role.${acc.accountRole}`, { defaultValue: acc.accountRole })}
                                            </span>
                                            {/* 商用帳號標所屬經銷商＋席次（零用量列也要看得到歸屬） */}
                                            {role === 'business' && acc.createdBy && emailByUid[acc.createdBy] && (
                                                <span className="text-[11px] text-ink-400">{t('biz.ownedBy', { email: emailByUid[acc.createdBy] })}</span>
                                            )}
                                            {role === 'business' && acc.seats && (
                                                <span className="text-[11px] text-ink-400 tnum">AA {acc.seats.plan_01 ?? 0} · CS {acc.seats.plan_02 ?? 0}</span>
                                            )}
                                            <span className="flex items-center gap-1.5 flex-wrap">
                                                {acc.tenants.map((tn) => (
                                                    <Link key={tn.hostId} to={`/admin/tenants/${tn.hostId}`}
                                                        className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-cream-100 text-ink-400 hover:bg-accent-50 hover:text-accent-700 transition">
                                                        {tn.hostId}
                                                    </Link>
                                                ))}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                );
            })()}
        </div>
    );
}

/** 搜尋框（帳號 email / 顯示名 / 租戶）—— 本地即時篩選，不打 API */
function SearchBox({ value, onChange, placeholder }) {
    return (
        <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
            <input
                type="search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-56 h-[34px] pl-8 pr-7 rounded-md border border-cream-300 bg-white text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-accent-400"
            />
            {value && (
                <button
                    onClick={() => onChange('')}
                    aria-label="clear"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-ink-700"
                >
                    <X size={12} />
                </button>
            )}
        </div>
    );
}

/**
 * 卡片上的「下載報表」—— 按下才跳月份選擇（PM 0916 指定）。
 *
 * 為什麼不沿用頁首那個 MonthPager：那個是控「這頁看哪個月的用量」，
 * 跟「我要下載哪個月的帳單」是兩件事 —— 超管常常在看本月用量、但要補印上個月的報表。
 * 預設帶頁首的月份當起點，省一次點擊。
 *
 * ⚠️ 只掛在商用帳號卡上。經銷商層沒有席次與免費額度的概念，報表出不了（後端也會擋）。
 */
function DownloadReportButton({ uid, defaultMonth, t }) {
    const { i18n } = useTranslation('superAdmin');
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(defaultMonth);
    const [busy, setBusy] = useState(false);

    async function go() {
        setBusy(true);
        try {
            await accountsApi.downloadReport(uid, month, { lang: i18n.language });
            setOpen(false);
        } catch (e) {
            alert(e.message);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="relative">
            <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
                title={t('biz.downloadReport')}
                aria-expanded={open}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/85 hover:bg-white text-ink-700 font-mono text-[9px] uppercase tracking-[0.14em] transition"
            >
                <Download size={11} /> {t('biz.downloadReport')}
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" {...overlayDismiss(() => setOpen(false))} />
                    <div className="absolute right-0 top-8 z-20 w-60 rounded-lg border border-cream-200 bg-white shadow-lg p-3">
                        <div className="text-[11px] text-ink-500 mb-2">{t('biz.pickMonth')}</div>
                        <div className="flex justify-center mb-3">
                            <MonthPager month={month} onChange={setMonth} />
                        </div>
                        <p className="text-[11px] text-ink-400 leading-relaxed mb-3">{t('biz.reportHint')}</p>
                        <button
                            onClick={go}
                            disabled={busy}
                            className="w-full h-8 rounded-md bg-accent-500 text-white text-[12px] font-medium hover:bg-accent-600 transition disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                            {t('biz.downloadExcel')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

/** 商用帳號卡 — 企業藍封面 + 可開角色/席次/經銷商 + 四格當月用量 + tenant 鏈 */
/** YYYY-MM-DD（UTC+8）—— 跟後端計費月份同一個時區 */
const todayTaipei = () => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);

/**
 * 調整既有帳號的席次（超管）。
 * 為什麼要有生效日：擴線常是事後才補登（1012 實案：9/8 擴成 5 線、10 月對帳才發現報表還是 3 線），
 * 只能記「今天」的話，擴線當月的免費額度就補不回來。
 */
function SeatsEditModal({ acc, t, onClose, onSaved }) {
    const cur = acc.seats || {};
    const [aa, setAa] = useState(String(cur.plan_01 ?? 0));
    const [cs, setCs] = useState(String(cur.plan_02 ?? 0));
    const [date, setDate] = useState(todayTaipei());
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const today = todayTaipei();
    const toInt = (v) => (/^\d+$/.test(String(v).trim()) ? Number(v) : NaN);
    const next = { plan_01: toInt(aa), plan_02: toInt(cs) };
    const valid = Number.isInteger(next.plan_01) && Number.isInteger(next.plan_02) && next.plan_01 + next.plan_02 > 0 && !!date && date <= today;   // 商用帳號至少 1 席
    const changed = next.plan_01 !== (cur.plan_01 ?? 0) || next.plan_02 !== (cur.plan_02 ?? 0);

    const save = async () => {
        setBusy(true); setErr(null);
        try {
            await superAdminApi.updateSeats(acc.uid, next, date === today ? undefined : date);
            onSaved(acc.uid, next);
            onClose();
        } catch (e) {
            setErr(e.message || String(e));
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 p-4" {...overlayDismiss(onClose)}>
            <div className="w-full max-w-sm rounded-xl bg-cream-50 border border-cream-200 shadow-xl p-5"
                onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="seats-edit-title">
                <h2 id="seats-edit-title" className="heading-display text-lg font-semibold text-ink-900">{t('seatsEdit.title')}</h2>
                <div className="mt-0.5 text-xs text-ink-500 truncate">{accountLabel(acc) || acc.email}</div>
                {/* 東訊授權的帳號：席次會在授權異動時被授權的數字覆蓋 → 提醒，但不鎖。
                    不鎖的原因（1013 實查 8 個東訊帳號）：授權到期不會觸發同步，席次會停在到期前的數字，
                    這種要靠手動校正；鎖死就沒有地方能改。 */}
                {acc.sdxManaged && (
                    <div className="mt-3 rounded-md border border-warn-500/40 bg-warn-100/20 px-3 py-2 text-[12px] leading-relaxed text-ink-700">
                        {t('seatsEdit.sdxNote')}{' '}
                        <Link to="/sdx500" className="text-accent-700 underline underline-offset-2">{t('seatsEdit.sdxLink')}</Link>
                    </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <Input label={t('create.seatAA')} className="tnum" type="number" min="0" step="1" inputMode="numeric" value={aa} onChange={(e) => setAa(e.target.value)} />
                    <Input label={t('create.seatCS')} className="tnum" type="number" min="0" step="1" inputMode="numeric" value={cs} onChange={(e) => setCs(e.target.value)} />
                </div>
                <div className="mt-3">
                    <Input label={t('seatsEdit.effectiveDate')} className="tnum" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-ink-400">{t('seatsEdit.hint')}</p>
                {err && <p className="mt-2 text-xs text-accent-700 break-words">{t('seatsEdit.failed', { reason: err })}</p>}
                <div className="mt-4 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>{t('create.cancel')}</Button>
                    <Button variant="accent" size="sm" onClick={save} disabled={busy || !valid || !changed}>
                        {busy && <Loader2 size={13} className="animate-spin" />}{t('seatsEdit.save')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function BusinessAccountCard({ acc, dealerEmail, month, t, onSeatsSaved }) {
    const [editingSeats, setEditingSeats] = useState(false);
    const cover = coverFromSeed(acc.email || acc.uid, 'business');
    // PM 0903 P7「補名稱」：以前封面只有 email（joan_hsu@ipcc.com.tw），認不出是哪一家。
    // 改成公司名當主標、email 降成第二行；沒有公司名的帳號維持原樣（email 當主標）。
    const label = accountLabel(acc);
    const rq = acc.roleQuota;
    const seats = acc.seats;
    const stats = [
        [t('data.stat.calls'), acc.totals?.calls ?? 0],
        [t('data.stat.minutes'), billMin(acc.totals)],
        [t('data.stat.inbound'), acc.totals?.inboundCalls ?? 0],
        [t('data.stat.outbound'), acc.totals?.outboundCalls ?? 0],
    ];
    return (
        <Card className="p-0 overflow-hidden" padded={false}>
            <div className="h-14 flex items-center gap-3 px-4" style={{ background: cover.gradient }}>
                <span className="heading-display text-xl font-semibold shrink-0" style={{ color: cover.fg }}>
                    {initialsOf(label || acc.email)}
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                    {label ? (
                        <>
                            <span className="block truncate text-sm font-semibold text-white drop-shadow-sm">{label}</span>
                            <span className="block truncate text-[11px] text-white/80 drop-shadow-sm">{acc.email}</span>
                        </>
                    ) : (
                        <span className="block truncate text-sm font-medium text-white/95 drop-shadow-sm">{acc.email}</span>
                    )}
                </span>
                <span className="ml-auto shrink-0 font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-white/85 text-ink-700">
                    {acc.sharedHostId ? t('overview.sharedBadge', { hostId: acc.sharedHostId }) : t('role.business')}
                </span>
                {/* 下載報表 —— PM 0903 P7：超管底下有直客，要能直接幫他們出月報表 */}
                <div className="shrink-0"><DownloadReportButton uid={acc.uid} defaultMonth={month} t={t} /></div>
            </div>
            <div className="p-4">
                {dealerEmail && <div className="text-[11px] text-ink-400 mb-2">{t('biz.ownedBy', { email: dealerEmail })}</div>}
                <div className="space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                        <span className="text-ink-400">{t('billing.roles')}</span>
                        <span className="tnum text-ink-700 font-medium">{rq ? <>AA {rq.plan_01 ?? 0} · CS {rq.plan_02 ?? 0}</> : '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-ink-400">{t('billing.col.seats')}</span>
                        <span className="flex items-center gap-1.5">
                            <span className="tnum text-ink-700">{seats ? <>AA {seats.plan_01 ?? 0} · CS {seats.plan_02 ?? 0}</> : '—'}</span>
                            {/* 共用租戶的子帳號沒有自己的席次（吃上層帳號的）→ 不給改 */}
                            {!acc.sharedHostId && (
                                <button type="button" onClick={() => setEditingSeats(true)} title={t('seatsEdit.title')} aria-label={t('seatsEdit.title')}
                                    className="grid place-items-center size-5 rounded text-ink-400 hover:text-accent-700 hover:bg-accent-50 transition">
                                    <Pencil size={11} />
                                </button>
                            )}
                        </span>
                    </div>
                </div>
                {editingSeats && <SeatsEditModal acc={acc} t={t} onClose={() => setEditingSeats(false)} onSaved={onSeatsSaved} />}
                <div className="mt-3 pt-3 border-t border-cream-100 grid grid-cols-4 gap-2">
                    {stats.map(([label, v]) => (
                        <div key={label}>
                            <div className="tnum text-base font-semibold text-ink-900 leading-none">{Number(v).toLocaleString()}</div>
                            <div className="mt-1 text-[10px] text-ink-400">{label}</div>
                        </div>
                    ))}
                </div>
                {acc.tenants.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-cream-100 flex items-center gap-1.5 flex-wrap">
                        {acc.tenants.map((tn) => (
                            <Link key={tn.hostId} to={`/admin/tenants/${tn.hostId}/calls`}
                                title={tn.hostId}
                                className="inline-flex items-baseline gap-1 text-[11px] px-1.5 py-0.5 rounded bg-cream-100 text-ink-600 hover:bg-accent-50 hover:text-accent-700 transition">
                                {/* 租戶名稱是「認得出是哪一家」的關鍵（實查 72 個租戶有 70 個有名稱），
                                    hostId 是機器代號 → 名稱在前、代號用小字跟在後面。 */}
                                {tn.displayName && tn.displayName !== tn.hostId
                                    ? <><span className="font-medium">{tn.displayName}</span><span className="font-mono text-[10px] text-ink-400">{tn.hostId}</span></>
                                    : <span className="font-mono">{tn.hostId}</span>}
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
}

/** tenant 用量卡 — 同超管中心卡片語言：封面 + 四格當月數據（商用帳號=藍色封面） */
function UsageCard({ tn, kind = 'full' }) {
    const { t } = useTranslation('superAdmin');
    const cover = coverFromSeed(tn.hostId, kind);
    return (
        // 數據頁點卡 → 該 tenant 的通話記錄（統計+明細+錄音）；AI 診斷從超管中心卡進
        <Link to={`/admin/tenants/${tn.hostId}/calls`} className="block group focus-visible:outline-2 focus-visible:outline-accent-500 rounded-xl">
            <Card className="p-0 overflow-hidden transition group-hover:border-accent-300 group-hover:shadow-md" padded={false}>
                <div className="h-16 flex items-center justify-center relative" style={{ background: cover.gradient }}>
                    <div className="heading-display text-2xl font-semibold" style={{ color: cover.fg }}>
                        {initialsOf(tn.displayName)}
                    </div>
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-white/85 backdrop-blur-[2px] font-mono text-[10px] text-ink-700">
                        {tn.hostId}
                    </div>
                    {tn.region && (
                        <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/85 backdrop-blur-[2px] font-mono text-[10px] uppercase text-ink-700">
                            <Globe2 size={10} /> {tn.region}
                        </div>
                    )}
                </div>
                <div className="p-4">
                    <div className="flex items-center text-sm font-semibold text-ink-900">
                        <span className="truncate">{tn.displayName}</span>
                        <ChevronRight size={14} className="ml-auto text-ink-300 group-hover:text-accent-500 group-hover:translate-x-0.5 transition-transform shrink-0" />
                    </div>
                    {tn.error ? (
                        <div className="mt-3 text-xs text-danger-500">{tn.error}</div>
                    ) : (
                        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
                            <Metric label={t('data.col.calls')} value={tn.calls} strong />
                            <Metric label={t('data.col.minutes')} value={billMin(tn)} />
                            <Metric label={t('data.col.in')} value={tn.inboundCalls} />
                            <Metric label={t('data.col.out')} value={tn.outboundCalls} />
                        </div>
                    )}
                </div>
            </Card>
        </Link>
    );
}

/** 經銷商用量比較 — 直條圖（分鐘），每根不同色（design-system 內的 flat 色盤） */
const CHART_COLORS = ['#c84a2c', '#5e7a4e', '#c4892d', '#5b7a96', '#8a6aa3', '#a4423b', '#3d8f8a', '#b5764a'];

/** 圖上最多畫幾根 —— 46 個帳號全擠在一條 X 軸上，標籤會整片疊在一起（0918 回報） */
const CHART_MAX_BARS = 12;
/** X 軸標籤字數上限；中文公司名常是 6–10 字，超過就截斷加省略號 */
const CHART_LABEL_MAX = 8;

function UsageChart({ accounts, t }) {
    const all = accounts
        .filter((a) => (a.totals?.calls || 0) > 0)
        .map((a) => {
            const full = accountLabel(a) || (a.email || '').split('@')[0];
            return {
                name: a.email,
                full,
                short: full.length > CHART_LABEL_MAX ? `${full.slice(0, CHART_LABEL_MAX)}…` : full,
                minutes: billMin(a.totals),
                calls: a.totals?.calls || 0,
                inbound: a.totals?.inboundCalls || 0,
                outbound: a.totals?.outboundCalls || 0,
            };
        })
        .sort((x, y) => y.minutes - x.minutes);
    // 長尾幾乎都是 0 分鐘，畫出來只有標籤沒有柱子 —— 砍掉才看得清楚前幾名
    const rows = all.slice(0, CHART_MAX_BARS);
    const hidden = all.length - rows.length;
    return (
        <div className="rounded-lg border border-cream-200 bg-white/40 p-4">
            <ResponsiveContainer width="100%" height={260}>
                <BarChart data={rows} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#ede4cf" strokeDasharray="2 4" />
                    <XAxis dataKey="short" tick={{ fontSize: 11, fill: '#3d362d' }} axisLine={{ stroke: '#e0d4ba' }} tickLine={false} interval={0} />
                    <YAxis tick={{ fontSize: 10, fill: '#8a7f72' }} axisLine={false} tickLine={false} width={44} />
                    <Tooltip
                        cursor={{ fill: '#f5eedf', opacity: 0.5 }}
                        contentStyle={{ background: '#fbf8f1', border: '1px solid #e0d4ba', borderRadius: 8, fontSize: 12 }}
                        formatter={(v, k, item) => {
                            const p = item?.payload || {};
                            return [`${v.toLocaleString()} ${t('data.col.minutes')}（${p.calls} ${t('data.col.calls')}・${t('data.col.in')} ${p.inbound}・${t('data.col.out')} ${p.outbound}）`, ''];
                        }}
                        labelFormatter={(_, payload) => {
                            const p = payload?.[0]?.payload;
                            return p ? `${p.full}（${p.name}）` : '';   // 截斷的標籤看不出是誰 → tooltip 給完整名稱 + email
                        }}
                    />
                    <Bar dataKey="minutes" radius={[4, 4, 0, 0]} maxBarSize={48}>
                        {rows.map((r, i) => (
                            <Cell key={r.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
            {hidden > 0 && (
                <div className="mt-1 text-[11px] text-ink-400">{t('data.chartTopN', { n: rows.length, hidden })}</div>
            )}
        </div>
    );
}

function Metric({ label, value, strong }) {
    const zero = !value;
    return (
        <div>
            <div className={`tnum text-lg leading-none ${zero ? 'text-ink-300' : strong ? 'font-semibold text-ink-900' : 'text-ink-700'}`}>
                {(value || 0).toLocaleString()}
            </div>
            <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-ink-400">{label}</div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="flex-1 px-6 py-4 first:pl-1">
            <div className="tnum heading-display text-3xl font-semibold text-ink-900 leading-none">{value}</div>
            <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400">{label}</div>
        </div>
    );
}
